import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Modal,
  Space,
  Tag,
  Typography,
  message
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined
} from '@ant-design/icons';
import type { RouteViewModel } from '../../types/routeViewModel';
import {
  useGetExecutionProgressQuery,
  useUpsertRouteStopsMutation
} from '../../store/api/routesApi';
import { buildMutationPolicyPreview, mapRejectMessage, policyFeedbackFromApi } from './buildMutationPolicyPreview';
import {
  buildMutationTimelineFromAudits,
  buildMutationTimelineFromHydration
} from './buildMutationTimelinePanel';
import { MutationImpactPreviewPanel } from './MutationImpactPreviewPanel';
import { MutationReasonField } from './MutationReasonField';
import { MutationStopEditorModal } from './MutationStopEditorModal';
import { MutationTimelinePanel } from './MutationTimelinePanel';
import {
  draftToSaveRow,
  mergeStopsForSave,
  splitStopsByExecution,
  stopLabel,
  viewStopToDraft
} from './mutationStopHelpers';
import type { MutationStopDraft } from './types';
import './mutationWorkspace.css';

const { Text } = Typography;

function countAffectedCargo(
  allocations: Record<string, unknown>[] | undefined,
  stop: MutationStopDraft
): number {
  if (!allocations?.length) return 0;
  let count = 0;
  for (const a of allocations) {
    const from = Number((a as { fromLoadingPosition?: unknown }).fromLoadingPosition);
    const to = Number((a as { toUnloadingPosition?: unknown }).toUnloadingPosition);
    if (stop.type === 'loading' && from === stop.position) count++;
    if (stop.type === 'unloading' && to === stop.position) count++;
  }
  return count;
}

function newStopDraft(type: 'loading' | 'unloading', position: number): MutationStopDraft {
  return {
    uid: `new-${type}-${Date.now()}`,
    type,
    position,
    address: null,
    city: null,
    contactPerson: null,
    phone: null,
    plannedAt: null,
    notes: null
  };
}

type Props = {
  routeId: number;
  view: RouteViewModel;
  onSuccess: () => void;
};

export function RouteMutationWorkspaceTab({ routeId, view, onSuccess }: Props) {
  const [baseline, setBaseline] = useState<MutationStopDraft[]>([]);
  const [draftStops, setDraftStops] = useState<MutationStopDraft[]>([]);
  const [mutationReason, setMutationReason] = useState<string | null>(null);
  const [confirmationProvided, setConfirmationProvided] = useState(false);
  const [editorStop, setEditorStop] = useState<MutationStopDraft | null>(null);
  const [editorMode, setEditorMode] = useState<'add' | 'edit'>('edit');
  const [deleteTarget, setDeleteTarget] = useState<MutationStopDraft | null>(null);
  const [lastPolicyMessage, setLastPolicyMessage] = useState<string | null>(null);

  const { data: executionProgress, refetch: refetchExecution } = useGetExecutionProgressQuery(routeId);
  const [upsertStops, { isLoading: saving }] = useUpsertRouteStopsMutation();

  const syncFromView = useCallback(() => {
    const stops = (view.structure.stops ?? []).map((s, i) => viewStopToDraft(s, i));
    setBaseline(stops);
    setDraftStops(stops);
    setMutationReason(null);
    setConfirmationProvided(false);
    setLastPolicyMessage(null);
  }, [view.structure.stops]);

  useEffect(() => {
    syncFromView();
  }, [syncFromView]);

  const { executed, future } = useMemo(() => splitStopsByExecution(draftStops), [draftStops]);
  const isDirty = useMemo(
    () => JSON.stringify(baseline) !== JSON.stringify(draftStops),
    [baseline, draftStops]
  );

  const preview = useMemo(
    () =>
      buildMutationPolicyPreview({
        baseline,
        draft: mergeStopsForSave(executed, future)
      }),
    [baseline, executed, future]
  );

  const timelineItems = useMemo(() => {
    const fromHydration = buildMutationTimelineFromHydration(
      executionProgress?.operationalTimeline?.entries
    );
    if (fromHydration.length) return fromHydration;
    return buildMutationTimelineFromAudits(
      executionProgress?.recentOperationalAudits as
        | Array<{ action: string; createdAt: string; context?: unknown; newState?: unknown }>
        | undefined
    );
  }, [executionProgress]);

  const openAdd = (type: 'loading' | 'unloading') => {
    const nextPos = draftStops.length;
    const stop = newStopDraft(type, nextPos);
    setEditorMode('add');
    setEditorStop(stop);
  };

  const applyEditor = (stop: MutationStopDraft) => {
    if (editorMode === 'add') {
      setDraftStops((prev) => [...prev, { ...stop, position: prev.length }]);
    } else {
      setDraftStops((prev) => prev.map((s) => (s.uid === stop.uid ? stop : s)));
    }
    setEditorStop(null);
  };

  const moveFuture = (uid: string, direction: 'up' | 'down') => {
    const futureUids = future.map((s) => s.uid);
    const idx = futureUids.indexOf(uid);
    if (idx < 0) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= future.length) return;

    const reordered = [...future];
    [reordered[idx], reordered[swapIdx]] = [reordered[swapIdx], reordered[idx]];

    const executedPositions = executed.map((s) => s.position);
    const minFuturePos = executedPositions.length ? Math.max(...executedPositions) + 1 : 0;
    const withPositions = reordered.map((s, i) => ({ ...s, position: minFuturePos + i }));
    setDraftStops([...executed, ...withPositions]);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setDraftStops((prev) => prev.filter((s) => s.uid !== deleteTarget.uid));
    setDeleteTarget(null);
  };

  const handleSave = async () => {
    if (!mutationReason) {
      message.error('Укажите причину изменения');
      return;
    }
    if (preview.status === 'forbidden') {
      message.error(preview.message ?? 'Изменение запрещено');
      return;
    }
    if (
      (preview.requiresConfirmation || preview.status === 'confirmation_required') &&
      !confirmationProvided
    ) {
      message.error('Подтвердите изменение');
      return;
    }
    if (!isDirty) {
      message.info('Нет изменений для сохранения');
      return;
    }

    const payload = mergeStopsForSave(executed, future).map(draftToSaveRow);

    try {
      const result = await upsertStops({
        routeId,
        stops: payload,
        mutationReason,
        confirmationProvided: confirmationProvided || undefined
      }).unwrap();

      const apiPreview = policyFeedbackFromApi(result.liveTopology, undefined);

      message.success('Изменения маршрута сохранены');
      setMutationReason(null);
      setConfirmationProvided(false);
      setLastPolicyMessage(apiPreview.message);
      onSuccess();
      await refetchExecution();
    } catch (e: unknown) {
      const err = e as { data?: unknown };
      const msg = mapRejectMessage(err.data) ?? 'Не удалось сохранить изменения';
      setLastPolicyMessage(msg);
      message.error(msg);
    }
  };

  const deleteCargoCount = deleteTarget
    ? countAffectedCargo(view.structure.allocations, deleteTarget)
    : 0;

  return (
    <div className="mutation-workspace">
      <Alert
        type="info"
        showIcon
        message="Изменение маршрута"
        description="Редактируются только будущие точки. Выполненные точки доступны только для просмотра."
      />

      <div className="mutation-workspace__columns">
        <div className="mutation-workspace__main">
          <div className="mutation-workspace__section-title">Выполнено</div>
          {executed.length === 0 ? (
            <Text type="secondary">Нет выполненных точек</Text>
          ) : (
            <div className="mutation-workspace__executed-list">
              {executed.map((stop) => (
                <div
                  key={stop.uid}
                  className="mutation-workspace__stop-row mutation-workspace__stop-row--executed"
                >
                  <Space>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    <Tag>{stop.type === 'loading' ? 'Погрузка' : 'Выгрузка'}</Tag>
                    <Text>{stopLabel(stop)}</Text>
                  </Space>
                </div>
              ))}
            </div>
          )}

          <div className="mutation-workspace__section-title" style={{ marginTop: 24 }}>
            Будущие точки
          </div>
          <div className="mutation-workspace__toolbar">
            <Button icon={<PlusOutlined />} onClick={() => openAdd('loading')}>
              Погрузка
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => openAdd('unloading')}>
              Выгрузка
            </Button>
          </div>

          {future.length === 0 ? (
            <Text type="secondary">Нет будущих точек</Text>
          ) : (
            <div className="mutation-workspace__future-list">
              {future.map((stop, idx) => (
                <div
                  key={stop.uid}
                  className="mutation-workspace__stop-row mutation-workspace__stop-row--future"
                >
                  <Space direction="vertical" size={0}>
                    <Space>
                      <Tag color="blue">{stop.type === 'loading' ? 'Погрузка' : 'Выгрузка'}</Tag>
                      <Text strong>{stopLabel(stop)}</Text>
                    </Space>
                    {stop.plannedAt && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(stop.plannedAt).toLocaleDateString('ru-RU')}
                      </Text>
                    )}
                  </Space>
                  <div className="mutation-workspace__stop-actions">
                    <Button
                      size="small"
                      icon={<ArrowUpOutlined />}
                      disabled={idx === 0}
                      onClick={() => moveFuture(stop.uid, 'up')}
                      title="Вверх"
                    />
                    <Button
                      size="small"
                      icon={<ArrowDownOutlined />}
                      disabled={idx === future.length - 1}
                      onClick={() => moveFuture(stop.uid, 'down')}
                      title="Вниз"
                    />
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditorMode('edit');
                        setEditorStop(stop);
                      }}
                    />
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setDeleteTarget(stop)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mutation-workspace__sidebar">
          <MutationReasonField value={mutationReason} onChange={setMutationReason} disabled={saving} />

          {isDirty && (
            <MutationImpactPreviewPanel
              preview={preview}
              confirmationProvided={confirmationProvided}
              onConfirmationChange={setConfirmationProvided}
            />
          )}

          {lastPolicyMessage && (
            <Alert type="warning" message={lastPolicyMessage} showIcon closable />
          )}

          <div>
            <div className="mutation-workspace__section-title">История изменений</div>
            <MutationTimelinePanel items={timelineItems} />
          </div>
        </div>
      </div>

      <div className="mutation-workspace__footer">
        {isDirty && (
          <Text type="secondary" className="mutation-workspace__dirty-hint">
            Есть несохранённые изменения
          </Text>
        )}
        <Button onClick={syncFromView} disabled={!isDirty || saving}>
          Сбросить
        </Button>
        <Button type="primary" loading={saving} onClick={handleSave} disabled={!isDirty}>
          Сохранить изменения
        </Button>
      </div>

      <MutationStopEditorModal
        open={editorStop != null}
        stop={editorStop}
        title={editorMode === 'add' ? 'Новая точка' : 'Редактирование точки'}
        onCancel={() => setEditorStop(null)}
        onSave={applyEditor}
      />

      <Modal
        open={deleteTarget != null}
        title="Удаление точки"
        okText="Удалить"
        okButtonProps={{ danger: true }}
        onCancel={() => setDeleteTarget(null)}
        onOk={confirmDelete}
      >
        {deleteTarget && (
          <div>
            <p>
              Будет удалена точка: <Text strong>{stopLabel(deleteTarget)}</Text>
            </p>
            <p>Затронуты грузы: {deleteCargoCount}</p>
            <p>Требуется пересчёт:</p>
            <ul>
              <li>Execution</li>
              <li>Coordination</li>
              <li>Occupancy</li>
            </ul>
          </div>
        )}
      </Modal>
    </div>
  );
}
